/**
 * FillBlankPrint - Figma-matching print renderer for fill-in-the-blank quizzes
 * Layout: PrintQuizTitle → image → problem text with inline blank boxes (h-40px w-88px)
 * Problem lines: gap-12px between lines
 */

import type { ReactNode } from 'react'
import { cn } from '../../../lib/utils'
import { KatexText } from '../shared'
import type { FillBlankQuizData } from '../../../types/legacyQuiz'
import { formatProblem, countBlanks } from '../../../utils/quiz-parser'
import { PRINT_CORRECT_ANSWER_CLASS, PrintQuizTitle, PrintQuizImage, getStyles, getImageUrl, getPrintScale } from './print-common'

interface PrintQuizProps {
  quizId: string | number
  quiz: FillBlankQuizData
  number: number
  showCorrectAnswers?: boolean
  correctAnswers?: string[]
  compact?: boolean
}

export function FillBlankPrint({
  quizId,
  quiz,
  number,
  showCorrectAnswers = false,
  correctAnswers,
  compact,
}: PrintQuizProps) {
  const s = getStyles(compact)
  const imageUrl = getImageUrl(quiz.image, quiz.imagePrint)
  const printScale = getPrintScale(quiz.image, quiz.imagePrint)
  const blankCount = countBlanks(quiz.problem)

  // Render problem with inline blank boxes
  const renderedProblem: ReactNode[] = blankCount > 0
    ? formatProblem(
        quiz.problem,
        `${quizId}-print-problem`,
        (index: number) => {
          const answer = correctAnswers?.[index]
          const showAnswer = showCorrectAnswers && answer

          return (
            <span
              key={`blank-print-${quizId}-${index}`}
              className={cn(
                s.inputBoxLg,
                'inline-flex items-center justify-center px-[8px] align-middle text-[16px]',
                compact ? 'mx-[8px] my-[4px]' : 'mx-[12px]',
                showAnswer ? PRINT_CORRECT_ANSWER_CLASS : ''
              )}
            >
              {showAnswer ? <KatexText text={answer} /> : '\u00A0'}
            </span>
          )
        },
        quiz.additionalImages ?? undefined
      )
    : []

  return (
    <div className={cn(s.container, s.quizSection)}>
      {/* Title */}
      <PrintQuizTitle number={number} compact={compact}>
        <KatexText text={quiz.title} />
      </PrintQuizTitle>

      {/* Image */}
      {imageUrl && <PrintQuizImage src={imageUrl} printScale={printScale} />}

      {/* Problem with inline blanks — needs taller line-height to fit inline boxes */}
      {blankCount > 0 && (
        <div className={s.problemArea}>
          <div className={cn(
            s.problemLine,
            compact
              ? 'text-[15px] text-black tracking-[-0.09px] font-normal leading-[41px]'
              : s.problemText
          )}>
            {renderedProblem}
          </div>
        </div>
      )}
    </div>
  )
}
